// src/pages/Paystack.jsx

import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";

const Paystack = () => {
  const location = useLocation();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Confirming your subscription payment.");
  const [reference, setReference] = useState("");
  const [clientId, setClientId] = useState("");
  const [copied, setCopied] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const whatsappNumber = "2348069804062";

  const successWhatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Doctor, I have completed my subscription payment and would like to continue my consultation.`
    );
  }, []);

  const failedWhatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Zaviramed, I need help with my subscription payment verification. My payment reference is: ${
        reference || "not available"
      }. My client ID is: ${clientId || "not available"}.`
    );
  }, [reference, clientId]);

  const emailHref = useMemo(() => {
    const subject = encodeURIComponent("Subscription Payment Verification Support");
    const body = encodeURIComponent(
      `Hello Zaviramed,\n\nI need help with my subscription payment verification.\n\nPayment reference: ${
        reference || "not available"
      }\nClient ID: ${clientId || "not available"}\n\nPlease assist me.`
    );

    return `mailto:support@zaviramed.com.ng?subject=${subject}&body=${body}`;
  }, [reference, clientId]);

  const handleCallback = async ({ retry = false } = {}) => {
    try {
      if (retry) {
        setIsRetrying(true);
      }

      setStatus("loading");
      setMessage("Confirming your subscription payment.");

      const query = new URLSearchParams(location.search);
      const paymentReference = query.get("reference");
      const queryClientId = query.get("clientId");

      if (!paymentReference) {
        setStatus("error");
        setReference("");
        setMessage(
          "We could not find a valid payment reference for this transaction."
        );
        return;
      }

      setReference(paymentReference);
      setClientId(queryClientId || "");

      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (!backendUrl) {
        setStatus("error");
        setMessage(
          "Payment verification is not configured correctly. Please contact Zaviramed support."
        );
        return;
      }

      const res = await axios.post(
        `${backendUrl}/api/paystack/callback`,
        {
          reference: paymentReference,
          clientId: queryClientId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

      if (res.data?.success) {
        setStatus("success");
        setMessage(
          res.data?.message ||
            "Your subscription payment has been confirmed and your Zaviramed plan has been activated."
        );
      } else {
        setStatus("error");
        setMessage(
          res.data?.message ||
            "Payment verification failed. If you were debited, please contact Zaviramed support with your payment reference."
        );
      }
    } catch (err) {
      console.error("Paystack callback error:", err);

      setStatus("error");

      if (err.code === "ECONNABORTED") {
        setMessage(
          "Payment verification took too long. Please check your internet connection and retry."
        );
        return;
      }

      setMessage(
        err.response?.data?.message ||
          "We could not verify this payment at the moment. If you were debited, please contact Zaviramed support with your payment reference."
      );
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const openSuccessWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${successWhatsappText}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openFailedWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${failedWhatsappText}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openEmailSupport = () => {
    window.location.href = emailHref;
  };

  const copyReference = async () => {
    if (!reference) return;

    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch (err) {
      console.error("Could not copy reference:", err);
    }
  };

  const pageState = {
    loading: {
      label: "Verification in progress",
      title: "Confirming your subscription payment",
      description:
        "Please wait while we securely verify your transaction and activate your Zaviramed subscription.",
      badgeClass: "border-slate-200 bg-slate-50 text-slate-700",
      iconWrapClass: "border-slate-200 bg-slate-50 text-slate-700",
      Icon: Loader2,
    },
    success: {
      label: "Payment confirmed",
      title: "Your subscription is active",
      description:
        "Your payment has been verified. You can now continue your consultation and access Zaviramed subscription benefits.",
      badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
      iconWrapClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
      Icon: CheckCircle2,
    },
    error: {
      label: "Verification failed",
      title: "We could not confirm this payment",
      description:
        "This may happen if the payment is still processing, the reference is invalid, or the request could not reach the server.",
      badgeClass: "border-red-200 bg-red-50 text-red-700",
      iconWrapClass: "border-red-200 bg-red-50 text-red-700",
      Icon: XCircle,
    },
  };

  const current = pageState[status] || pageState.loading;
  const StatusIcon = current.Icon;

  return (
    <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
      <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
          {/* Left Panel */}
          <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div>
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-12 w-auto object-contain"
              />

              <div className="mt-16">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                  <ShieldCheck size={15} />
                  Secure payment verification
                </div>

                <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                  Subscription payment confirmation.
                </h1>

                <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                  We verify your payment before activating your unlimited
                  Zaviramed consultation access.
                </p>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-slate-300">
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Service</span>
                <span className="font-medium text-white">Subscription</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Provider</span>
                <span className="font-medium text-white">Zaviramed</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Access</span>
                <span className="font-medium text-white">Unlimited consults</span>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <section className="p-5 sm:p-8 md:p-10 lg:p-12">
            <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-10 w-auto object-contain"
              />

              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <ShieldCheck size={14} />
                Secure
              </div>
            </div>

            <div
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${current.badgeClass}`}
            >
              {status === "loading" ? (
                <Loader2 size={14} className="animate-spin" />
              ) : status === "success" ? (
                <CheckCircle2 size={14} />
              ) : (
                <AlertCircle size={14} />
              )}

              {current.label}
            </div>

            <div className="mt-8 max-w-2xl">
              <div
                className={`mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border ${current.iconWrapClass}`}
              >
                <StatusIcon
                  size={32}
                  className={status === "loading" ? "animate-spin" : ""}
                />
              </div>

              <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                {current.title}
              </h2>

              <p className="mt-4 text-base leading-7 text-slate-600">
                {current.description}
              </p>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm leading-6 text-slate-700">{message}</p>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="mt-8 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Transaction details
                </p>
              </div>

              <div className="grid gap-0 sm:grid-cols-2">
                <div className="border-b border-slate-200 px-4 py-4 sm:border-b-0 sm:border-r">
                  <p className="text-xs font-medium text-slate-500">
                    Payment purpose
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">
                    Unlimited consultation subscription
                  </p>
                </div>

                <div className="border-b border-slate-200 px-4 py-4 sm:border-b-0">
                  <p className="text-xs font-medium text-slate-500">
                    Client ID
                  </p>
                  <p className="mt-1 truncate text-sm font-semibold text-slate-950">
                    {clientId || "Not available"}
                  </p>
                </div>

                <div className="border-t border-slate-200 px-4 py-4 sm:col-span-2">
                  <p className="text-xs font-medium text-slate-500">
                    Payment reference
                  </p>

                  <div className="mt-1 flex items-center gap-2">
                    <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-950">
                      {reference || "Not available"}
                    </p>

                    {reference && (
                      <button
                        type="button"
                        onClick={copyReference}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        <Copy size={13} />
                        {copied ? "Copied" : "Copy"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {status === "success" && (
                <button
                  type="button"
                  onClick={openSuccessWhatsapp}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0F8F5F] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#0B7A50] active:scale-[0.99] sm:w-auto"
                >
                  <MessageCircle size={18} />
                  Continue on WhatsApp
                </button>
              )}

              {status === "error" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleCallback({ retry: true })}
                    disabled={isRetrying}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-4 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                  >
                    {isRetrying ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <RefreshCw size={18} />
                    )}
                    Retry verification
                  </button>

                  <button
                    type="button"
                    onClick={openEmailSupport}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-50 active:scale-[0.99] sm:w-auto"
                  >
                    <MessageCircle size={18} />
                     support
                  </button>

               
                </>
              )}

              {status === "loading" && (
                <button
                  type="button"
                  disabled
                  className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-slate-200 px-5 py-4 text-sm font-semibold text-slate-500 sm:w-auto"
                >
                  <Loader2 size={18} className="animate-spin" />
                  Verifying payment
                </button>
              )}
            </div>

            <div className="mt-10 grid gap-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  If your account was debited but verification failed, contact
                  Zaviramed support with your payment reference.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  Once confirmed, your subscription will allow continued access
                  to Zaviramed consultations based on your active plan.
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default Paystack;